import {Injectable, Injector} from '@angular/core';
import {Store} from '@ngrx/store';
import {firstValueFrom, lastValueFrom} from 'rxjs';
import {selectOrgId} from '../store/organisation/organisation.selector';
import {jwtDecode} from 'jwt-decode';
import {AuthService} from '@app/public/auth/service/auth.service';
import {refreshToken as refreshAction} from '../store/auth/auth.actions';
import {TokenStorageService} from './token-storage.service';

interface JWTPayload {
    exp: number;
    [key: string]: any;
}

@Injectable({
    providedIn: 'root',
})
export class AuthHeaderService {

    private authService: AuthService | undefined; // Lazy-loaded AuthService

    constructor(
        private injector: Injector,
        private store: Store,
        private tokenStorageService: TokenStorageService
    ) { }

    /**
        * Handles concurrent API requests when token is about to expire.
        *
        * If multiple requests are made while the token is expiring, we want only one
        * to actually trigger the refresh operation. Others should wait until the
        * refresh is complete to avoid redundant network calls and race conditions.
        *
        * This check ensures:
        * - Only one refresh request is made at a time.
        * - All other requests wait for the refresh to finish before proceeding.
        *
        * `refreshingToken` is a localStorage flag:
        * - 'yes': token is currently being refreshed by some other request.
        * - null/undefined: no refresh is in progress, safe to proceed or start a new refresh.
    */
    async appendAuthToken(headers: Record<string, string>, context: any): Promise<Record<string, string>> {
        if (context?.skipAuth) {
            return headers;
        }

        // Use TokenStorageService to get the appropriate token for this tab
        let token = this.tokenStorageService.getCurrentToken();

        if (token && this.isTokenExpiringSoon(token, 60)) {
            const refreshKey = this.tokenStorageService.isImpersonationMode() ? 'refreshingImpersonatedToken' : 'refreshingToken';

            if (sessionStorage.getItem(refreshKey) === 'yes') {
                // Wait for other request to refresh and return new token
                await this.checkTokenIsRefreshed(refreshKey);
                const updatedToken = this.tokenStorageService.getCurrentToken();
                return {
                  ...headers,
                  authorization: `Bearer ${updatedToken}`,
                };
            }

            sessionStorage.setItem(refreshKey, 'yes');

            if (!this.authService) {
                this.authService = this.injector.get(AuthService); // Lazy load AuthService to avoid circular dependencies
            }

            const refreshToken = this.tokenStorageService.getCurrentRefreshToken();

            try {
                const response = await lastValueFrom(this.authService.refreshToken({ refreshToken: refreshToken }));
                const newToken = response.data.refreshToken;
                if (newToken) {
                    this.store.dispatch(refreshAction({ token: newToken }));
                    token = newToken;
                }
            } catch (err) {
                this.authService.logout();
            }

            sessionStorage.removeItem(refreshKey);
        }

        if (token) {
            return {
                ...headers,
                authorization: `Bearer ${token}`,
            };
        }

        return headers;
    }

    async appendCustomHeaders(headers: Record<string, string>, context: any): Promise<Record<string, string>> {
        // Never attach Organisation header on admin routes/tabs
        if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
            return headers;
        }

        if (!context?.skipOrg) {
            let orgId = await firstValueFrom(this.store.select(selectOrgId));

            // Fallback: if store hasn’t populated orgId yet, use storage-based value
            if (!orgId) {
                const storage = this.tokenStorageService.isImpersonationMode() ? sessionStorage : localStorage;
                orgId = storage.getItem('orgId');
            }

            if (orgId) {
                return {
                    ...headers,
                    Organisation: orgId || '',
                };
            }
        }
        return headers;
    }

    isTokenExpiringSoon(token: string, bufferInSeconds: number = 60): boolean {
        try {
          const decoded = jwtDecode<JWTPayload>(token);
          const currentTime = Math.floor(Date.now() / 1000); // current time in seconds
          return decoded.exp - currentTime < bufferInSeconds;
        } catch (e) {
          return true; // assume expired if can't decode
        }
    }
    sleep(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    async checkTokenIsRefreshed(refreshKey: string): Promise<boolean> {
        while (sessionStorage.getItem(refreshKey)) {
            await this.sleep(500);
        }
        return true;
    }
}
